'use client';

import type {
  ContextBinding,
  DataSourceConfig,
  FormComponent,
} from '@commenda/types';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { DSLContextMarker } from '../../../constants';
import { FormContext } from '../../../hooks/FormContext';
import { AutoDetectFieldComponent } from '../AutoDetectField';
import { BusinessEntityAdditionalFieldsProps } from './types';

export const ENTITY_CONTEXT_MARKER = DSLContextMarker.ENTITY_CONTEXT;

/**
 * BusinessEntityAdditionalFields Component
 *
 * Renders additional AutoDetect fields from the template prop with entity-specific context.
 * This component is used for additional fields beyond the required business entity fields
 * (Legal Name, Country, Jurisdiction, Entity Type).
 *
 * This component handles:
 * - Overriding context bindings to use the specific entity's ID
 * - Generating unique field IDs per entity
 * - Handling "Add New" flow (no entity ID yet)
 * - Pre-populating values from entity data for existing entities
 */
export const BusinessEntityAdditionalFields = ({
  parentFieldId,
  entityId,
  isNewEntity,
  template,
  entities,
  valueField = 'id',
}: BusinessEntityAdditionalFieldsProps) => {
  const { dslContext, formData, updateField } = useContext(FormContext);

  // Helper to get nested value from object
  const getNestedValue = useCallback(
    (obj: Record<string, unknown>, path: string): unknown => {
      return path.split('.').reduce((current: unknown, key: string) => {
        if (current && typeof current === 'object') {
          return (current as Record<string, unknown>)[key];
        }
        return undefined;
      }, obj);
    },
    []
  );

  // Find the current entity from the entities list
  const currentEntity = useMemo(() => {
    if (isNewEntity || !entities) return null;
    return (
      entities.find(
        (e) => String(getNestedValue(e, valueField)) === entityId
      ) || null
    );
  }, [entities, entityId, isNewEntity, valueField, getNestedValue]);

  // Pre-populate form data with entity values for existing entities
  useEffect(() => {
    if (isNewEntity || !currentEntity || template.length === 0) return;

    template.forEach((field) => {
      const entityFieldId = `${parentFieldId}-entity-${entityId}-${field.fieldId || field.id}`;
      const readPath = field.dataSource?.commendaOS?.field?.read;

      // Only pre-populate if:
      // 1. We have a read path to get the value from
      // 2. The form data doesn't already have a value for this field
      if (readPath && formData[entityFieldId] === undefined) {
        const entityValue = getNestedValue(currentEntity, readPath);
        if (
          entityValue !== undefined &&
          entityValue !== null &&
          entityValue !== ''
        ) {
          updateField(entityFieldId, entityValue);
        }
      }
    });
  }, [
    currentEntity,
    isNewEntity,
    template,
    parentFieldId,
    entityId,
    formData,
    updateField,
    getNestedValue,
  ]);

  // Build the entity-specific context
  // For existing entities, we have the ID
  // For new entities, we don't have an ID yet (will be created on submit)
  const entityContext = useMemo(() => {
    if (isNewEntity) {
      // For new entities, don't include the entity ID in context
      // The submit logic will handle creating the entity first
      return { ...dslContext };
    }

    // For existing entities, add the entity ID to context
    return {
      ...dslContext,
      corporationId: Number(entityId),
    };
  }, [dslContext, entityId, isNewEntity]);

  // Transform template fields to have entity-specific field IDs and context bindings
  const transformedTemplate = useMemo(() => {
    return template.map((field) => {
      // Generate a unique field ID for this entity
      const entityFieldId = `${parentFieldId}-entity-${entityId}-${field.fieldId || field.id}`;

      // Transform the data source to use entity context
      let transformedDataSource: DataSourceConfig | undefined =
        field.dataSource;

      if (field.dataSource?.commendaOS) {
        if (isNewEntity) {
          // For new entities, remove context bindings entirely
          // The fields should be enabled for data entry (creating a new entity)
          transformedDataSource = {
            ...field.dataSource,
            commendaOS: {
              ...field.dataSource.commendaOS,
              // Clear context bindings for new entities - fields should be enabled
              contextBindings: [],
            },
          };
        } else {
          // For existing entities, use the entity's ID as context
          const newContextBindings: ContextBinding[] = [
            {
              contextKey: 'corporationId',
              sourceFieldId: ENTITY_CONTEXT_MARKER, // Special marker
            },
          ];

          // Preserve any other context bindings that aren't for the corporation ID
          const existingBindings =
            field.dataSource.commendaOS.contextBindings || [];
          const otherBindings = existingBindings.filter(
            (b) => b.contextKey !== 'corporationId'
          );

          transformedDataSource = {
            ...field.dataSource,
            commendaOS: {
              ...field.dataSource.commendaOS,
              contextBindings: [...newContextBindings, ...otherBindings],
            },
          };
        }
      }

      return {
        ...field,
        fieldId: entityFieldId,
        dataSource: transformedDataSource,
        // Store metadata about the entity context for submit logic
        props: {
          ...field.props,
          __entityContext__: {
            parentFieldId,
            entityId,
            entityType: 'corporation',
            isNewEntity,
            originalFieldId: field.fieldId || field.id,
          },
        },
      };
    });
  }, [template, parentFieldId, entityId, isNewEntity]);

  if (template.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 mt-4 pt-4 border-t border-border-secondary">
      <p className="text-xs text-text-tertiary font-medium uppercase tracking-wider">
        Additional Information
      </p>
      {transformedTemplate.map((field) => {
        // Render AutoDetectField components
        if (
          field.component === 'AutoDetectField' ||
          field.dataSource?.commendaOS
        ) {
          return (
            <AutoDetectFieldWithEntityContext
              key={field.id}
              field={field}
              entityContext={entityContext}
              isNewEntity={isNewEntity}
            />
          );
        }

        // Fallback for other component types (shouldn't happen in normal use)
        return (
          <div key={field.id} className="text-sm text-text-tertiary">
            Unsupported field type: {field.component}
          </div>
        );
      })}
    </div>
  );
};

/**
 * AutoDetectFieldWithEntityContext
 *
 * Wrapper that provides entity-specific context to AutoDetectField
 */
interface AutoDetectFieldWithEntityContextProps {
  field: FormComponent;
  entityContext: Record<string, unknown>;
  isNewEntity: boolean;
}

const AutoDetectFieldWithEntityContext = ({
  field,
  entityContext,
  isNewEntity,
}: AutoDetectFieldWithEntityContextProps) => {
  return (
    <AutoDetectFieldComponent
      fieldId={field.fieldId!}
      label={field.label}
      placeholder={field.placeholder}
      guidance={field.guidance}
      description={field.description}
      validation={field.validation}
      dataSource={field.dataSource}
      props={{
        ...field.props,
        // Pass entity context as props for submit logic to use
        __entityContext__: {
          ...(field.props?.__entityContext__ as Record<string, unknown>),
          dslContext: entityContext,
          isNewEntity,
        },
      }}
    />
  );
};
